<script lang="ts" module>
  // Target is a picked destination (spec 003 FR-021, 003 FR-022).
  export interface Target {
    name: string;
    lat: number;
    lon: number;
    place_id?: string;
  }
</script>

<script lang="ts">
  import { addCredits } from '../map/credits';
  import { onDestroy, onMount, untrack } from 'svelte';
  import * as maplibregl from 'maplibre-gl';
  import { api } from '../../api/client';
  import type { MapInfo, Place } from '../../api/types';
  import { iconClass } from '../../utils/icons';
  import { palette } from '../../utils/palette';
  import Modal from '../Modal.svelte';
  import PlaceSearch, { type Found } from '../places/PlaceSearch.svelte';
  import { followTheme, northUpMap, styleFor } from '../map/theme';
  import { t } from '../../i18n/index.ts';

  // DestinationPicker picks where someone is going: the viewer's own
  // location, a group place, a search result, or a spot clicked on the map.
  // It opens on start, the viewer's own location for "How long until…"
  // (spec 003).
  interface Props {
    title: string;
    confirm: string;
    start?: { lat: number; lon: number };
    // mine offers "Your location" as a destination and picks it first.
    mine: boolean;
    places: Place[];
    onPick: (t: Target) => Promise<void>;
    onclose: () => void;
  }
  let { title, confirm, start, mine, places, onPick, onclose }: Props = $props();

  // YOURS names the viewer's own location in a picked target. It is not shown:
  // the member panel and the map recognize it and word it themselves.
  const YOURS = 'your location';

  // dropped is set while the pin is on a spot clicked on the map. Its name is
  // only shown here; the server names the spot (003 FR-059).
  let dropped = $state(false);

  // The picker opens on the viewer's own location when offered.
  let chosen: Target | null = $state(untrack(() => (mine && start ? { name: YOURS, lat: start.lat, lon: start.lon } : null)));
  let busy = $state(false);
  let error = $state('');
  let mapEl: HTMLDivElement | undefined = $state();
  let map: maplibregl.Map | null = null;
  let marker: maplibregl.Marker | null = null;
  let stopTheme: (() => void) | null = null;

  onMount(async () => {
    const info = await api.get<MapInfo>('/map').catch(() => null);
    if (!mapEl) return;
    const c = start ?? places[0] ?? { lat: 39.8, lon: -98.5 };
    map = northUpMap({ container: mapEl, style: styleFor(info), center: [c.lon, c.lat], zoom: start || places.length ? 13 : 3, attributionControl: false });
    addCredits(map, info, 'other');
    stopTheme = followTheme(map, info, () => draw());
    map.on('click', (e) => {
      choose({ name: t('componentsDestinationDroppedPin'), lat: e.lngLat.lat, lon: e.lngLat.lng }, false);
      dropped = true;
    });
  });

  onDestroy(() => {
    stopTheme?.();
    map?.remove();
  });

  function draw() {
    if (!map) return;
    if (!chosen) {
      marker?.remove();
      marker = null;
      return;
    }
    if (!marker) marker = new maplibregl.Marker({ color: palette('red') }).setLngLat([chosen.lon, chosen.lat]).addTo(map);
    marker.setLngLat([chosen.lon, chosen.lat]);
  }

  function choose(target: Target, move = true) {
    chosen = target;
    dropped = false;
    draw();
    if (move) map?.easeTo({ center: [target.lon, target.lat], zoom: Math.max(map.getZoom(), 14) });
  }

  const center = () => {
    const c = map?.getCenter();
    return c ? { lat: c.lat, lon: c.lng } : start ?? null;
  };

  async function submit() {
    if (!chosen) return;
    busy = true;
    error = '';
    try {
      await onPick(dropped ? { ...chosen, name: '' } : chosen);
      onclose();
    } catch (e: any) {
      error = e.message;
    } finally {
      busy = false;
    }
  }
</script>

<Modal {title} {onclose} maxWidth="lg" maxHeight="90vh">
  <div class="space-y-3 overflow-y-auto">
    <div bind:this={mapEl} class="h-56 rounded-lg overflow-hidden" aria-label={t('componentsDestinationMapLabel')}></div>
    <PlaceSearch near={center} onPick={(f: Found) => choose({ name: f.name.slice(0, 64), lat: f.lat, lon: f.lon })} />
    <ul class="choices">
      {#if mine && start}
        <li>
          <button type="button" class="choice" class:on={chosen?.name === YOURS} onclick={() => choose({ name: YOURS, lat: start!.lat, lon: start!.lon })}>
            <i aria-hidden="true" class="las la-crosshairs"></i><span>{t('componentsDestinationYours')}</span>
          </button>
        </li>
      {/if}
      {#each places as p (p.id)}
        <li>
          <button type="button" class="choice" class:on={chosen?.place_id === p.id} onclick={() => choose({ name: p.name, lat: p.lat, lon: p.lon, place_id: p.id })}>
            <i aria-hidden="true" class={iconClass(p.icon)}></i><span>{p.name}</span>
          </button>
        </li>
      {/each}
    </ul>
    {#if chosen}
      <p class="text-sm"><i aria-hidden="true" class="las la-flag-checkered mr-1" style="color: var(--theme-accent);"></i>{chosen.name === YOURS ? t('componentsDestinationYours') : chosen.name}</p>
    {:else}
      <p class="text-sm text-text-muted">{t('componentsDestinationHint')}</p>
    {/if}
    {#if error}<p class="text-sm" style="color: var(--theme-error);">{error}</p>{/if}
    <div class="flex justify-end gap-2">
      <button type="button" class="btn btn-secondary" onclick={onclose}>{t('appCancel')}</button>
      <button type="button" class="btn btn-primary" disabled={!chosen || busy} onclick={submit}>{confirm}</button>
    </div>
  </div>
</Modal>

<style>
  .choices {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
  }
  .choice {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.3rem 0.7rem;
    border-radius: 9999px;
    font-size: 0.85rem;
    border: 1px solid var(--theme-surface1);
    background: none;
    color: var(--theme-text);
    cursor: pointer;
  }
  .choice i {
    color: var(--theme-accent);
  }
  .choice.on {
    border-color: var(--theme-accent);
    background: color-mix(in srgb, var(--theme-accent) 15%, transparent);
  }
</style>
